// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: 'React' is declared but its value is never read
import React, {
	createContext,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import FlagGroup from '@atlaskit/flag/flag-group';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { steps } from './spotlight-engagement-survey';
import { type JSX } from 'react/jsx-runtime';

interface SurveyContextType {
	showSurvey: () => void;
}

export const SurveyContext: React.Context<SurveyContextType> = createContext<SurveyContextType>({
	showSurvey: () => undefined,
});

export const SpotlightEngagementSurveyProvider = ({
	children,
}: {
	children: ReactNode;
}): JSX.Element | null => {
	const [isVisible, setIsVisible] = useState<boolean>(false);
	const showSurvey = useCallback(() => setIsVisible(true), []);
	const value = useMemo(() => ({ showSurvey }), [showSurvey]);
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedValues, setSelectedValues] = useState<string[]>(['', '', '', '', '']);
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	// Fire the viewed event when survey becomes visible
	useEffect(() => {
		if (isVisible) {
			// https://data-portal.internal.atlassian.com/analytics/registry/93061
			fireAnalyticsEvent({
				eventType: 'screen',
				name: 'adsSpotlightSurveyModal',
			});
		}
	}, [isVisible, fireAnalyticsEvent]);

	const setSelectedValue = (stepIndex: number, value: string) => {
		setSelectedValues((prev) => {
			const updated = [...prev];
			updated[stepIndex] = value;
			return updated;
		});
	};

	const fireSubmitEvent = useCallback(
		(actionSubjectId: string, option: string) => {
			// https://data-portal.internal.atlassian.com/analytics/registry/95165
			fireAnalyticsEvent({
				eventType: 'track',
				action: 'submitted',
				actionSubject: 'adsSpotlightSurvey',
				actionSubjectId,
				attributes: {
					option,
					questionId: actionSubjectId,
				},
			});
		},
		[fireAnalyticsEvent],
	);

	const Step = steps[currentStep];

	if (!Step) {
		return null;
	}

	return (
		<>
			<SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>

			{/*
				Using `FlagGroup` to correctly position the the custom `Flag`.
				However, there will only ever be one `Flag`. So we can just hardcode
				a `key={1}` and we won't run into any collision.
			*/}
			{isVisible && (
				<FlagGroup>
					<Step
						step={currentStep}
						next={() => setCurrentStep(currentStep + 1)}
						back={() => setCurrentStep(currentStep - 1)}
						done={() => setIsVisible(false)}
						dismiss={() => setIsVisible(false)}
						selectedValues={selectedValues}
						setSelectedValue={setSelectedValue}
						fireSubmitEvent={fireSubmitEvent}
					/>
				</FlagGroup>
			)}
		</>
	);
};
